import { type FixedDepartureDetail, type FixedDepartureInclusionOut } from '@utazzo/shared';
import { UzButton, UzInput } from '@utazzo/ui';
import { Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import {
  useCreateFixedDepartureExclusion,
  useCreateFixedDepartureInclusion,
  useDeleteFixedDepartureExclusion,
  useDeleteFixedDepartureInclusion,
  useUpdateFixedDepartureExclusion,
  useUpdateFixedDepartureInclusion,
} from '../use-fixed-departures';

interface TextListProps {
  heading: string;
  description: string;
  placeholder: string;
  rows: ReadonlyArray<FixedDepartureInclusionOut>;
  onAdd: (text: string) => Promise<unknown>;
  onUpdate: (rowId: string, text: string) => Promise<unknown>;
  onDelete: (rowId: string) => void;
  adding: boolean;
}

/** One editable line list. Inclusions and exclusions are the same shape. */
const TextList = ({
  heading,
  description,
  placeholder,
  rows,
  onAdd,
  onUpdate,
  onDelete,
  adding,
}: TextListProps): React.JSX.Element => {
  const [newText, setNewText] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const submitNew = async (): Promise<void> => {
    const text = newText.trim();
    if (!text) return;
    await onAdd(text);
    setNewText('');
  };

  const submitEdit = async (): Promise<void> => {
    const text = editText.trim();
    if (!editingId || !text) return;
    await onUpdate(editingId, text);
    setEditingId(null);
  };

  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-5">
      <h2 className="text-sm font-semibold text-zinc-900">{heading}</h2>
      <p className="mt-0.5 text-xs text-zinc-500">{description}</p>

      <ul className="mt-4 space-y-1.5">
        {rows.map((row) => (
          <li key={row.id} className="flex items-center gap-2">
            {editingId === row.id ? (
              <>
                <UzInput
                  className="flex-1"
                  value={editText}
                  autoFocus
                  onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void submitEdit();
                    if (e.key === 'Escape') setEditingId(null);
                  }}
                />
                <UzButton size="sm" onClick={submitEdit} disabled={!editText.trim()}>
                  Save
                </UzButton>
                <UzButton size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                  Cancel
                </UzButton>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="min-w-0 flex-1 rounded px-2 py-1.5 text-left text-sm text-zinc-800 transition-colors hover:bg-zinc-50"
                  onClick={() => {
                    setEditingId(row.id);
                    setEditText(row.text);
                  }}
                >
                  {row.text}
                </button>
                <UzButton
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-zinc-400 hover:text-red-600"
                  onClick={() => onDelete(row.id)}
                  aria-label="Remove line"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </UzButton>
              </>
            )}
          </li>
        ))}
        {rows.length === 0 && (
          <li className="px-2 py-1.5 text-sm text-zinc-400">Nothing added yet.</li>
        )}
      </ul>

      <div className="mt-3 flex items-center gap-2">
        <UzInput
          className="flex-1"
          placeholder={placeholder}
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submitNew();
          }}
        />
        <UzButton size="sm" onClick={submitNew} disabled={!newText.trim()} loading={adding}>
          <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          Add
        </UzButton>
      </div>
    </section>
  );
};

/**
 * Inclusions and exclusions. Plain ordered lines - the payment plan is derived
 * from the existing payment-terms logic at itinerary time, so there is nothing
 * to enter for it here.
 */
export const FdInclusionsStep = ({ fd }: { fd: FixedDepartureDetail }): React.JSX.Element => {
  const createInclusion = useCreateFixedDepartureInclusion(fd.id);
  const updateInclusion = useUpdateFixedDepartureInclusion(fd.id);
  const deleteInclusion = useDeleteFixedDepartureInclusion(fd.id);
  const createExclusion = useCreateFixedDepartureExclusion(fd.id);
  const updateExclusion = useUpdateFixedDepartureExclusion(fd.id);
  const deleteExclusion = useDeleteFixedDepartureExclusion(fd.id);

  return (
    <div className="space-y-6">
      <TextList
        heading="Inclusions"
        description="What the price covers. One line per point, in the order they should read."
        placeholder="e.g. Daily breakfast at the hotel"
        rows={fd.inclusions}
        adding={createInclusion.isPending}
        onAdd={(text) => createInclusion.mutateAsync({ text })}
        onUpdate={(rowId, text) => updateInclusion.mutateAsync({ rowId, payload: { text } })}
        onDelete={(rowId) => deleteInclusion.mutate(rowId)}
      />
      <TextList
        heading="Exclusions"
        description="What the price does not cover."
        placeholder="e.g. Airfare and airport taxes"
        rows={fd.exclusions}
        adding={createExclusion.isPending}
        onAdd={(text) => createExclusion.mutateAsync({ text })}
        onUpdate={(rowId, text) => updateExclusion.mutateAsync({ rowId, payload: { text } })}
        onDelete={(rowId) => deleteExclusion.mutate(rowId)}
      />
    </div>
  );
};
